/**
 * OrbitPrint — accept an invitation. Shows who invited you and to what; signed
 * in with the right email → Accept; otherwise routes to sign in / register with
 * the invite kept in the URL so it completes afterwards.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useOrbitAuth, useOrbitPath, orbitApi, errText } from '../OrbitAuthContext';
import { AuthShell, OrbitButton, ArrowIcon } from '../components/OrbitShell';

export default function OrbitAcceptInvitePage() {
  const { user, loading, applySession } = useOrbitAuth();
  const p = useOrbitPath();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [inv, setInv] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError('This invitation link is incomplete.'); return; }
    orbitApi.get(`/invites/${token}`).then((r) => setInv(r.data)).catch((err) => setError(errText(err, 'Invitation not found.')));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const r = await orbitApi.post('/invites/accept', { token });
      applySession(r.data);
      toast.success(r.data.message);
      navigate(p('/'));
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };

  const q = `?invite=${encodeURIComponent(token)}`;
  const sameEmail = user && inv && user.email?.toLowerCase() === inv.email;

  return (
    <AuthShell title={inv ? `Join ${inv.org_name}` : 'Invitation'}
      lead={inv ? `${inv.invited_by_name || 'A manager'} invited ${inv.email} as ${inv.role === 'admin' ? 'an admin' : 'a member'}.` : undefined}
      footer={<span><Link to={p('/login')} className="orbit-link">Back to sign in</Link></span>}>
      {error ? (
        <div className="orbit-panel p-5 text-sm orbit-ink2" data-testid="orbit-invite-error">{error}</div>
      ) : !inv || loading ? (
        <p className="orbit-mono text-sm orbit-muted">Loading…</p>
      ) : inv.status !== 'pending' ? (
        <div className="orbit-panel p-5 text-sm orbit-ink2">This invitation is {inv.status}. Ask {inv.invited_by_name || 'the manager'} for a new one.</div>
      ) : sameEmail ? (
        <OrbitButton block onClick={accept} busy={busy} testid="orbit-invite-accept">Accept and open the dashboard <ArrowIcon /></OrbitButton>
      ) : user ? (
        <div className="orbit-panel p-5 text-sm orbit-ink2" data-testid="orbit-invite-wrong-account">
          You're signed in as <span className="orbit-mono">{user.email}</span>, but this invitation is for <span className="orbit-mono">{inv.email}</span>.
          <div className="mt-3"><Link to={`${p('/login')}${q}`} className="orbit-link">Sign in with that email</Link></div>
        </div>
      ) : (
        <div className="grid gap-3">
          <Link to={`${p('/register')}${q}&email=${encodeURIComponent(inv.email)}`} className="orbit-btn is-primary is-block" data-testid="orbit-invite-register">Create an account <ArrowIcon /></Link>
          <Link to={`${p('/login')}${q}&email=${encodeURIComponent(inv.email)}`} className="orbit-btn is-quiet is-block" data-testid="orbit-invite-login">I already have one — sign in</Link>
        </div>
      )}
    </AuthShell>
  );
}
